/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: OpenVPNPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the openvpn module.
----------------------------------------------------------------*/


import React, { useState } from 'react';
import { Shield, Plus, RefreshCw, Settings } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useOpenVPN } from './hooks/useOpenVPN';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { SettingsTab } from './tabs/SettingsTab';
import { ClientsTab } from './tabs/ClientsTab';
import { CreateClientModal } from './components/CreateClientModal';
import type { CreateClientRequest } from './types';

type ViewType = 'clients' | 'settings';

export default function OpenVPNPage() {
    const { t } = useI18n();
    const [currentView, setCurrentView] = useState<ViewType>('clients');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const {
        // Config
        configFormData,
        loadingConfig,
        savingConfig,
        hasConfigChanges,
        updateConfigField,
        saveConfig,
        cancelConfigChanges,

        // Clients
        clients,
        loadingClients,
        creatingClient,
        createClient,
        deleteClient,
        revokeClient,
        downloadConfig,
        refreshStatus
    } = useOpenVPN();

    const handleSave = async () => {
        await saveConfig();
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await refreshStatus();
        setRefreshing(false);
    };

    const handleCreate = async (data: CreateClientRequest) => {
        const result = await createClient(data);
        if (result) {
            setShowCreateModal(false);
        }
    };

    const handleBackToList = () => {
        if (hasConfigChanges) {
            cancelConfigChanges();
        }
        setCurrentView('clients');
    };

    if (loadingConfig && loadingClients) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    // Settings/Configuration View
    if (currentView === 'settings') {
        return (
            <div className="p-6 pb-20">
                <ModuleFormHeader
                    icon={<Shield className="h-5 w-5 text-primary" />}
                    title={t('openvpn.tabs.settings', 'OpenVPN Settings')}
                    backButtonText={t('openvpn.backToClients', 'Back to Clients')}
                    onBack={handleBackToList}
                />

                <div className="mt-6">
                    <SettingsTab
                        formData={configFormData}
                        loading={loadingConfig}
                        updateField={updateConfigField}
                    />
                </div>

                <FixedActionBar
                    hasChanges={hasConfigChanges}
                    onSave={handleSave}
                    onCancel={cancelConfigChanges}
                    isSaving={savingConfig}
                    saveText={t('common.save', 'Save')}
                    cancelText={t('common.cancel', 'Cancel')}
                />
            </div>
        );
    }

    // Clients List View (Main View)
    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Shield className="h-5 w-5 text-primary" />}
                title={t('openvpn.title', 'OpenVPN Server')}
                backButtonText=""
                onBack={() => { }}
                hideBackButton={true}
            >
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                        {t('common.refresh', 'Refresh')}
                    </button>
                    <button
                        onClick={() => setCurrentView('settings')}
                        className="flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                        <Settings className="h-4 w-4" />
                        {t('openvpn.configuration', 'Configuration')}
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        {t('common.add', 'Add')}
                    </button>
                </div>
            </ModuleFormHeader>

            <div className="mt-10">
                <ClientsTab
                    clients={clients}
                    loading={loadingClients}
                    onDelete={deleteClient}
                    onRevoke={revokeClient}
                    onDownload={downloadConfig}
                />
            </div>

            {/* Create Client Modal */}
            <CreateClientModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreate={handleCreate}
                isCreating={creatingClient}
            />
        </div>
    );
}
